import React, { useEffect, useState } from 'react'
import { CircularProgress } from '@material-ui/core'
import { Text, Button } from '.'
import { Dialog } from './Dialog'
import { downloadFile } from 'shared/utils/downloadFile'

/**
 * A PDF in a large dialog, shown by the browser's own PDF viewer. The file is fetched and shown from a blob: URL, so it renders
 * even when the file server marks it as a download or forbids framing it. If it can't be fetched, the dialog offers to open the
 * file in a new tab instead.
 *
 * Props: `open`, `url` (the file), `title` (the dialog title, also the name a download is saved under), `onClose`.
 */
export function PdfViewerDialog({ open, url, title, onClose }) {
  const [state, setState] = useState({ status: 'loading', src: null })

  useEffect(() => {
    if (!open || !url) return undefined
    let alive = true
    let objectUrl = null
    setState({ status: 'loading', src: null })

    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.blob()
      })
      .then(blob => {
        if (!alive) return
        objectUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
        setState({ status: 'ready', src: objectUrl })
      })
      .catch(() => { if (alive) setState({ status: 'error', src: null }) })

    return () => {
      alive = false
      if (objectUrl) window.URL.revokeObjectURL(objectUrl)
    }
  }, [open, url])

  const fileName = /\.pdf$/i.test(title || '') ? title : `${title || 'document'}.pdf`

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      title={(
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text variant="h5" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{title}</Text>
          {/* both are the same kind of button as every dialog's close button, so they share one size and one centre line */}
          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginLeft: 10, marginRight: -10 }}>
            <Button kind="download" onClick={() => downloadFile(url, fileName)} />
            <Button kind="close" onClick={onClose} />
          </div>
        </div>
      )}
    >
      <div style={{ height: '75vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {state.status === 'loading' && <CircularProgress size={30} />}
        {state.status === 'ready' && (
          <iframe title={title || 'PDF'} src={state.src} style={{ width: '100%', height: '100%', border: 0 }} />
        )}
        {state.status === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <Text variant="body2" color="textMuted" style={{ marginBottom: 8 }}>Couldn't load the PDF here.</Text>
            <Button variant="text" color="primary" label="Open in a new tab" onClick={() => window.open(url, '_blank', 'noopener')} />
          </div>
        )}
      </div>
    </Dialog>
  )
}

export default PdfViewerDialog
